<template>
  <ActionDialog
    ref="dialog"
    @opened="getIntent"
    :title="title"
    :subtitle="subtitle"
    :action="doExport"
    :inactive="!valid"
    :done-text="doneText"
    :loading="loadingIntent"
    max-height="500"
    width="550"
    invert-highlight
    pad
  >
    <div class="text-subtitle-2">Timezone</div>
    <v-radio-group
      v-model="input.timezone"
      class="mt-0"
      :disabled="loadingIntent"
      density="compact"
      hide-details
    >
      <v-radio
        v-for="timezone in timezones"
        :key="timezone.value"
        :label="timezone.title"
        :value="timezone.value"
        density="compact"
      ></v-radio>
    </v-radio-group>

    <template v-if="!hideDateRange">
      <div class="text-subtitle-2 mt-4">Date range</div>
      <v-radio-group
        v-model="input.date"
        class="mt-0"
        :disabled="loadingIntent"
        density="compact"
        hide-details
      >
        <v-radio
          v-for="date in dateRanges"
          :key="date.value"
          :value="date.value"
          density="compact"
          class="w-full"
        >
          <template #label>
            <div class="grid grid-cols-12 gap-2 w-80vw md:w-400px">
              <div class="col-span-6">{{ date.title }}</div>
              <div v-if="date.subtitle" class="col-span-6">
                {{ date.subtitle }}
              </div>
            </div>
          </template>
        </v-radio>
      </v-radio-group>
      <v-scroll-y-transition>
        <div v-if="isCustomDate" class="ml-4">
          <v-menu
            ref="dateMenu"
            v-model="dateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ props }">
              <v-text-field
                v-model="customDateText"
                v-bind="props"
                label="Custom date range"
                prepend-icon="date_range"
                density="compact"
                readonly
                hide-details
              />
            </template>

            <v-confirm-edit
              v-model="customDate"
              @update:model-value="dateMenu = false"
            >
              <template v-slot:default="{ model: proxyModel, actions }">
                <v-date-picker v-model="proxyModel.value" multiple="range">
                  <template v-slot:actions>
                    <v-spacer></v-spacer>
                    <component :is="actions"></component>
                  </template>
                </v-date-picker>
              </template>
            </v-confirm-edit>
          </v-menu>
        </div>
      </v-scroll-y-transition>
    </template>

    <!-- args -->
    <template v-if="args.length">
      <div class="text-subtitle-2 mt-4">Options</div>
      <v-dynamic-form
        v-model="input.args"
        class="mt-2"
        :inputs="argsFromInput"
      />
    </template>

    <div class="text-subtitle-2 mt-4">Columns</div>
    <v-progress-circular
      v-if="loadingIntent"
      indeterminate
      width="1"
      size="20"
    />
    <template v-else>
      <v-combobox
        v-model="input.columns"
        :items="columns"
        item-title="name"
        item-value="key"
        density="compact"
        chips
        closable-chips
        multiple
        hide-details
      />
      <v-checkbox v-model="selectAllColumns" label="Select all" hide-details />
    </template>
  </ActionDialog>
  <v-dialog v-model="doingExport" width="500">
    <v-card height="auto" max-height="auto">
      <div class="px-4 py-2">
        <h1 class="text-h6">Export</h1>
      </div>
      <v-divider />
      <div class="pa-4 text-body-2">
        <div v-if="gettingExport" class="text-center py-5">
          <v-progress-circular indeterminate width="1" size="30" />
          <div class="mt-2">Preparing export…</div>
        </div>
        <template v-else-if="exportSuccess">
          Your export is ready. If you don't see your file, you can try
          downloading it again or copy the download URL.
        </template>
        <template v-else>
          We couldn’t find any data to export based on your selections. Please
          adjust your filters and try again.
        </template>
      </div>
      <v-divider />
      <div class="flex-align px-4 py-2">
        <CopyBtn
          v-if="exportSuccess && exportUrl"
          :text="exportUrl"
          title="Copy URL"
          size="30"
        />
        <v-spacer />
        <v-btn v-if="gettingExport" @click="cancel">Cancel</v-btn>
        <v-btn v-else @click="close" color="primary"> Close </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import {
  DateRange,
  dateRanges,
  timezones,
  useExport,
} from "../private/exporter";
import dayjs from "dayjs";
import { first, last } from "lodash";

export default defineComponent({
  name: "Exporter",
  props: {
    name: { type: String, required: true },
    title: { type: String, default: "Export" },
    subtitle: { type: String },
    doneText: { type: String, default: "Export" },
    action: { type: String, required: true },
    actionsNamespace: { type: String },
    defaultRange: { type: String as PropType<DateRange> },
    args: { type: Object as PropType<Record<string, string>> },
    hideDateRange: { type: Boolean, default: false },
  },
  setup(props) {
    const dialog = ref<any>();
    const handle = props.actionsNamespace
      ? useNamespacedEvent(props.actionsNamespace).handle
      : useAppEvent().handle;

    const export$ = useExport(props.name, {
      defaultDateRange: props.defaultRange,
    });

    const argsFromInput = computed(() => {
      return fieldsToFormInputs(export$.args.value, {
        hideName: true,
        props: {
          variant: "outlined",
          rounded: "md",
          density: "compact",
          clearable: true,
        },
      });
    });

    const customDate = computed({
      set(value: (Date | string)[]) {
        const start = first(value);
        const end = last(value);
        export$.input.value.customDate = [
          dayjs(start).format("YYYY-MM-DD"),
          dayjs(end).format("YYYY-MM-DD"),
        ];
      },
      get() {
        return export$.input.value.customDate.map((d) => dayjs(d).toDate());
      },
    });

    const open = () => {
      dialog.value?.open();
    };

    handle(props.action, open);

    if (props.args) {
      export$.input.value.args = props.args;
    }

    return {
      ...export$,
      dialog,
      customDate,
      argsFromInput,
    };
  },
  data: () => ({
    timezones: timezones(),
    dateRanges: dateRanges(),
    dateMenu: false,
  }),
});
</script>
